import * as React from 'react';
import * as autoBind from 'react-autobind';
import ReviewDetailsModal from '../ReviewDetails/ReviewDetailsModal';
import {BasicModal} from '@techsmith/tsc-cloud-style-guide';
import LocalVideoImporter from '../LocalVideoImporter/LocalVideoImporter';
import {ModalStates} from '../../globals/Constants';
import Strings from '../../globals/Strings';
import Wait from '../../utils/Wait';
import HotjarService from '../../services/HotjarService/HotjarService';
import FilenameParser from '../../utils/FilenameParser/FilenameParser';
import Constants from '../../globals/Constants';
import Themeable from '../../types/Themeable';
import {nullModalEventHandlersFactory} from '../../types/ModalEventHandlers';
import ModalEventHandlersModel from '../../types/ModalEventHandlers';
import ReviewModel from '../../types/ReviewModel';
import {History} from 'history';

const placeHolderText = 'Start Review';

const getInitialState = (): StartReviewModalState => {
   return {
      workflowStep: 1,
      selectedFile: null,
      title: placeHolderText,
      prompt: '',
      source: '',
      sourceUrl: '',
      reviewSourceFileNameWithoutExtension: '',
      isUploadComplete: false,
      reviewId: '',
      reviewByDate: null
   };
};

export default class StartReviewModal extends React.Component<StartReviewModalProps, StartReviewModalState> {
   readonly state: StartReviewModalState = getInitialState();

   constructor(props: StartReviewModalProps) {
      super(props);
      autoBind(this);
   }

   onFileSelect(file: File): void {
      const uploadedFileNameWithoutExtension = FilenameParser.getFileNameWithoutExtension(file.name, Constants.title.maxLength);
      this.setState({workflowStep: 2, selectedFile: file, title: '', reviewSourceFileNameWithoutExtension: uploadedFileNameWithoutExtension});
   }

   onResetUpload(title: string, prompt: string, reviewByDate: Date): void {
      this.setState({workflowStep: 1, isUploadComplete: false, source: '', sourceUrl: '', title: title, prompt: prompt, reviewByDate: reviewByDate});
   }

   async onUploadComplete(source: string, url: string): Promise<void> {
      await Wait.seconds(1.5);
      this.setState({isUploadComplete: true, source: source || '', sourceUrl: url || ''});
   }

   resetState(): void {
      const {workflowStep} = this.state;

      HotjarService.startReviewModalClosed(workflowStep);

      if (workflowStep === 2) {
         this.setState(getInitialState());
      }
   }

   onCreateFinished(): void {
      this.resetState();
      this.props.setModalState(ModalStates.shareReviewWithCreatedHeader, nullModalEventHandlersFactory());
   }

   onCancel(): void {
      this.props.onClose();
   }

   getModalTitle(): string {
      const {workflowStep} = this.state;
      const {modalState, review} = this.props;

      if (workflowStep === 1) {
         if (modalState === ModalStates.newReview) {
            return Strings.titles.uploadMedia;
         } else if (modalState === ModalStates.newVersion) {
            return Strings.labels.versions.newVersionUploadModalText;
         }
      } else if (workflowStep === 2) {
         if (modalState === ModalStates.newReview) {
            return Strings.titles.addDetails;
         } else if (modalState === ModalStates.newVersion && review) {
            return Strings.labels.versions.newVersionDetailsModalText.replace('{param0}', (review.LatestVersionNumber + 1).toString());
         }
      }
      return placeHolderText;
   }

   render(): React.ReactElement<any> {
      const {visible, themeName, modalState, review, history} = this.props;
      const {workflowStep, title, isUploadComplete, prompt, source, sourceUrl, reviewSourceFileNameWithoutExtension, selectedFile, reviewByDate} = this.state;
      const titleString = ((title !== placeHolderText && title) || reviewSourceFileNameWithoutExtension || title);

      return (
         <BasicModal visible={visible} title={this.getModalTitle()} onClose={this.onCancel} themeName={themeName} testId="start-review-modal">
            <div className="modal-body">
               {workflowStep === 1 && <LocalVideoImporter onFileSelect={this.onFileSelect} themeName={themeName} />}

               {workflowStep === 2 && <ReviewDetailsModal
                  onUploadComplete={this.onUploadComplete}
                  onBackClick={this.onResetUpload}
                  onNextClick={this.onCreateFinished}
                  uploaded={isUploadComplete}
                  title={titleString}
                  prompt={prompt}
                  uploadSource={source}
                  uploadUrl={sourceUrl}
                  file={selectedFile}
                  reviewByDate={reviewByDate}
                  themeName={themeName}
                  visible={visible}
                  modalState={modalState}
                  review={review}
                  history={history} />}
            </div>
         </BasicModal>
      );

   }
}

interface StartReviewModalProps extends Themeable {
   modalState: ModalStates;
   visible: boolean;
   review: ReviewModel;
   history: History;
   onClose(): void;
   setModalState(modalState: ModalStates, modalEventHandlersModel: ModalEventHandlersModel): void;
}

interface StartReviewModalState {
   workflowStep: number;
   prompt: string;
   title: string;
   reviewByDate: Date;
   reviewId: string;
   isUploadComplete: boolean;
   source: string;
   sourceUrl: string;
   reviewSourceFileNameWithoutExtension: string;
   selectedFile: File;
}
